import React, { useState } from 'react';
import type { FormEvent } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { useSubmitContactForm } from '../../hooks/useContactForm';

export interface GetInTouchWidgetProps {
  title?: string;
  onSubmit?: (data: {
    name: string;
    email: string;
    phone: string;
    category: string;
    message: string;
  }) => void;
  vendorId?: number;
  locationId?: number;
  embedId?: string;
}

const GetInTouchWidget: React.FC<GetInTouchWidgetProps> = ({
  title = 'Get In Touch',
  onSubmit,
  vendorId = 298,
  locationId = 0,
  embedId
}) => {
  const submitContactForm = useSubmitContactForm();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interest: '',
    message: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { value: string } }) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpiar error del campo cuando el usuario empieza a escribir
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validar nombre (requerido)
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    // Validar email (requerido y formato)
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Validar teléfono (opcional, pero si se proporciona debe tener formato válido)
    if (formData.phone.trim() && !/^[\d\s\-\+\(\)]+$/.test(formData.phone.trim())) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    // Validar interés (opcional, pero si se proporciona debe ser válido)
    // No es requerido según el HTML original

    // Validar mensaje (opcional)
    // No es requerido según el HTML original

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Validar formulario antes de enviar
    if (!validateForm()) {
      return;
    }

    try {
      // Mapear "interest" a "category" y obtener el texto completo
      const categoryMap: Record<string, string> = {
        'buying': 'Buying a Home',
        'selling': 'Selling a Home',
        'investment': 'Investment Properties',
        'valuation': 'Home Valuation',
        'consultation': 'General Consultation',
        'other': 'Other'
      };
      
      const category = formData.interest ? categoryMap[formData.interest] || formData.interest : '';

      // Enviar usando el hook useSubmitContactForm
      await submitContactForm.mutateAsync({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        category: category,
        vendorId: vendorId,
        locationId: locationId,
        createdBy: 'CONTACT_FORM',
        type: 'getintouch',
        embedId: embedId
      });

      // Limpiar errores y resetear formulario
      setErrors({});
      setFormData({
        name: '',
        email: '',
        phone: '',
        interest: '',
        message: ''
      });

      // Call callback if provided
      if (onSubmit) {
        onSubmit({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          category: category,
          message: formData.message
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  return (
    <Box
      sx={{
        maxWidth: 520,
        margin: '0 auto',
        padding: { xs: '0 16px', sm: '0 20px' },
        fontFamily: "'Montserrat', sans-serif",
        '& *': {
          fontFamily: "'Montserrat', sans-serif",
        },
      }}
    >
      <link 
        href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&display=swap" 
        rel="stylesheet" 
      />
      
      <Typography
        variant="h2"
        component="h2"
        sx={{
          margin: { xs: '0 0 18px', sm: '0 0 14px' },
          fontSize: { xs: '32px', sm: '38px', md: '44px' },
          lineHeight: 1.05,
          fontWeight: 600,
          color: '#0e2340',
          textAlign: { xs: 'center', sm: 'left' },
        }}
      >
        {title}
      </Typography>
      
      <Box 
        component="form" 
        onSubmit={handleSubmit} 
        noValidate
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 1.25,
        }}
      >
        {/* Name Field */}
        <Box>
            <TextField
              fullWidth
              type="text"
              name="name"
              placeholder="Your Name"
              value={formData.name}
              onChange={handleChange('name')}
              required
              error={!!errors.name}
              helperText={errors.name}
              disabled={submitContactForm.isPending}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: '15px',
                  border: '2px solid #0e2340',
                  '& fieldset': {
                    border: 'none',
                  },
                  '&:hover': {
                    borderColor: '#0e2340',
                    '& fieldset': {
                      border: 'none',
                    },
                  },
                  '&.Mui-focused': {
                    borderColor: '#b08b4f',
                    boxShadow: '0 0 0 3px rgba(176, 139, 79, 0.15)',
                    '& fieldset': {
                      border: 'none',
                    },
                  },
                  '&.Mui-error': {
                    borderColor: '#d32f2f',
                    '&:hover': {
                      borderColor: '#d32f2f',
                      '& fieldset': {
                        border: 'none',
                      },
                    },
                    '&.Mui-focused': {
                      borderColor: '#d32f2f',
                      boxShadow: '0 0 0 3px rgba(211, 47, 47, 0.15)',
                      '& fieldset': {
                        border: 'none',
                      },
                    },
                  },
                },
                '& .MuiInputBase-input': {
                  padding: '12px 14px',
                  fontSize: '15px',
                  '&::placeholder': {
                    color: 'rgba(14, 35, 64, 0.55)',
                    fontSize: '14px',
                    opacity: 1,
                  },
                },
                '& .MuiFormHelperText-root': {
                  marginTop: '4px',
                  marginLeft: '2px',
                  fontSize: '12px',
                  fontFamily: "'Montserrat', sans-serif",
                },
              }}
            />
          </Box>
          
          {/* Email Field */}
          <Box>
            <TextField
              fullWidth
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange('email')}
              required
              error={!!errors.email}
              helperText={errors.email}
              disabled={submitContactForm.isPending}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: '15px',
                  border: '2px solid #0e2340',
                  '& fieldset': {
                    border: 'none',
                  },
                  '&:hover': {
                    borderColor: '#0e2340',
                    '& fieldset': {
                      border: 'none',
                    },
                  },
                  '&.Mui-focused': {
                    borderColor: '#b08b4f',
                    boxShadow: '0 0 0 3px rgba(176, 139, 79, 0.15)',
                    '& fieldset': {
                      border: 'none',
                    },
                  },
                  '&.Mui-error': {
                    borderColor: '#d32f2f',
                    '&:hover': {
                      borderColor: '#d32f2f',
                      '& fieldset': {
                        border: 'none',
                      },
                    },
                    '&.Mui-focused': {
                      borderColor: '#d32f2f',
                      boxShadow: '0 0 0 3px rgba(211, 47, 47, 0.15)',
                      '& fieldset': {
                        border: 'none',
                      },
                    },
                  },
                },
                '& .MuiInputBase-input': {
                  padding: '12px 14px',
                  fontSize: '15px',
                  '&::placeholder': {
                    color: 'rgba(14, 35, 64, 0.55)',
                    fontSize: '14px',
                    opacity: 1,
                  },
                },
                '& .MuiFormHelperText-root': {
                  marginTop: '4px',
                  marginLeft: '2px',
                  fontSize: '12px',
                  fontFamily: "'Montserrat', sans-serif",
                },
              }}
            />
          </Box>
          
          {/* Phone Field */}
          <Box>
            <TextField
              fullWidth
              type="tel"
              name="phone"
              placeholder="Phone Number"
              value={formData.phone}
              onChange={handleChange('phone')}
              error={!!errors.phone}
              helperText={errors.phone}
              disabled={submitContactForm.isPending}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: '15px',
                  border: '2px solid #0e2340',
                  '& fieldset': {
                    border: 'none',
                  },
                  '&:hover': {
                    borderColor: '#0e2340',
                    '& fieldset': {
                      border: 'none',
                    },
                  },
                  '&.Mui-focused': {
                    borderColor: '#b08b4f',
                    boxShadow: '0 0 0 3px rgba(176, 139, 79, 0.15)',
                    '& fieldset': {
                      border: 'none',
                    },
                  },
                  '&.Mui-error': {
                    borderColor: '#d32f2f',
                    '&:hover': {
                      borderColor: '#d32f2f',
                      '& fieldset': {
                        border: 'none',
                      },
                    },
                    '&.Mui-focused': {
                      borderColor: '#d32f2f',
                      boxShadow: '0 0 0 3px rgba(211, 47, 47, 0.15)',
                      '& fieldset': {
                        border: 'none',
                      },
                    },
                  },
                },
                '& .MuiInputBase-input': {
                  padding: '12px 14px',
                  fontSize: '15px',
                  '&::placeholder': {
                    color: 'rgba(14, 35, 64, 0.55)',
                    fontSize: '14px',
                    opacity: 1,
                  },
                },
                '& .MuiFormHelperText-root': {
                  marginTop: '4px',
                  marginLeft: '2px',
                  fontSize: '12px',
                  fontFamily: "'Montserrat', sans-serif",
                },
              }}
            />
          </Box>
          
          {/* Interest Select */}
          <Box>
            <FormControl fullWidth>
              <Select
                value={formData.interest}
                onChange={(e) => handleChange('interest')(e)}
                displayEmpty
                disabled={submitContactForm.isPending}
                sx={{
                  borderRadius: '8px',
                  border: '2px solid #0e2340',
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: '15px',
                  padding: '12px 14px',
                  '&:hover': {
                    borderColor: '#0e2340',
                  },
                  '&.Mui-focused': {
                    borderColor: '#b08b4f',
                    boxShadow: '0 0 0 3px rgba(176, 139, 79, 0.15)',
                  },
                  '& .MuiSelect-select': {
                    padding: 0,
                    fontSize: formData.interest ? '15px' : '15px',
                    color: formData.interest ? '#0e2340' : 'rgba(14, 35, 64, 0.55)',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    display: 'none',
                  },
                }}
                renderValue={(selected) => {
                  if (!selected) {
                    return <Box sx={{ color: 'rgba(14, 35, 64, 0.55)', fontSize: '14px' }}>I'm Interested in…</Box>;
                  }
                  const options: Record<string, string> = {
                    'buying': 'Buying a Home',
                    'selling': 'Selling a Home',
                    'investment': 'Investment Properties',
                    'valuation': 'Home Valuation',
                    'consultation': 'General Consultation',
                    'other': 'Other'
                  };
                  return options[selected] || selected;
                }}
              >
                <MenuItem value="buying">Buying a Home</MenuItem>
                <MenuItem value="selling">Selling a Home</MenuItem>
                <MenuItem value="investment">Investment Properties</MenuItem>
                <MenuItem value="valuation">Home Valuation</MenuItem>
                <MenuItem value="consultation">General Consultation</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          {/* Message Textarea */}
          <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
            <TextField
              fullWidth
              multiline
              rows={6}
              name="message"
              placeholder="Tell us how we can help with your home journey…"
              value={formData.message}
              onChange={handleChange('message')}
              disabled={submitContactForm.isPending}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: '15px',
                  minHeight: '210px',
                  border: '2px solid #0e2340',
                  '& fieldset': {
                    border: 'none',
                  },
                  '&:hover': {
                    borderColor: '#0e2340',
                    '& fieldset': {
                      border: 'none',
                    },
                  },
                  '&.Mui-focused': {
                    borderColor: '#b08b4f',
                    boxShadow: '0 0 0 3px rgba(176, 139, 79, 0.15)',
                    '& fieldset': {
                      border: 'none',
                    },
                  },
                },
                '& .MuiInputBase-input': {
                  padding: '14px',
                  fontSize: '15px',
                  '&::placeholder': {
                    color: 'rgba(14, 35, 64, 0.55)',
                    fontSize: '14px',
                    opacity: 1,
                  },
                },
              }}
            />
          </Box>
          
          {/* Submit Button */}
          <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={submitContactForm.isPending}
              startIcon={submitContactForm.isPending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              sx={{
                padding: '16px 20px',
                borderRadius: '16px',
                backgroundColor: '#0e2340',
                color: '#ffffff',
                fontWeight: 700,
                fontSize: { xs: '17px', sm: '18px' },
                fontFamily: "'Montserrat', sans-serif",
                textTransform: 'none',
                marginTop: '8px',
                '&:hover': {
                  backgroundColor: '#b08b4f',
                  boxShadow: '0 8px 18px rgba(14, 35, 64, 0.18)',
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
                '&:disabled': {
                  opacity: 0.6,
                },
              }}
            >
              {submitContactForm.isPending ? 'Sending...' : 'Send Message'}
            </Button>
          </Box>
          
          {/* Success/Error Messages */}
          {submitContactForm.isSuccess && (
            <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
              <Alert 
                severity="success"
                sx={{
                  marginTop: '8px',
                  borderRadius: '8px',
                  fontFamily: "'Montserrat', sans-serif",
                }}
              >
                Thank you! Your message has been sent successfully.
              </Alert>
            </Box>
          )}
          
          {submitContactForm.isError && (
            <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
              <Alert 
                severity="error"
                sx={{
                  marginTop: '8px',
                  borderRadius: '8px',
                  fontFamily: "'Montserrat', sans-serif",
                }}
              >
                {submitContactForm.error instanceof Error 
                  ? submitContactForm.error.message 
                  : 'Sorry, there was an error sending your message. Please try again.'}
              </Alert>
            </Box>
          )}
      </Box>
    </Box>
  );
};

export default GetInTouchWidget;

